import {useEffect, useMemo, useRef, useState} from "react";
import AsyncSelect from "react-select/async";
import { components as selectComponents } from "react-select";
import { cachedFetch } from "../utils/cached-api-wrapper";
import Utils from "../utils/utils";

// Compose two react-select `styles` objects: each entry of `override` runs after
// the matching entry of `base`, taking the styled object as its `base`. Lets a
// caller tweak individual stylers (e.g. only `placeholder`) without redefining
// the whole shared style block.
const mergeStyles = (base = {}, override) => {
    if (!override) return base;
    const out = { ...base };
    for (const key of Object.keys(override)) {
        const baseFn = base[key];
        const overrideFn = override[key];
        out[key] = (b, state) => {
            const fromBase = typeof baseFn === 'function' ? baseFn(b, state) : b;
            return typeof overrideFn === 'function' ? overrideFn(fromBase, state) : fromBase;
        };
    }
    return out;
};

const defaultOptionMap = (option) => {
    if (typeof option !== "object" || option === null) return null;
    const name = option.name ?? option.value ?? "";
    const description = option.description ?? "";
    const label = option.label ?? `${name}${description ? ` (${description})` : ""}`;
    return {
        value: name,
        label,
        name,
        description,
    };
};

const FormAsyncSelect = ({
                             data,
                             searchUrl,
                             placeholder = "",
                             onChange,
                             value,
                             isDisabled = false,
                             isClearable = false,
                             className = "",
                             highlightAll = false,
                             optionMap = defaultOptionMap,
                             showLabelInInput = true,
                             formatOptionLabel,
                             addOptionCallback,
                             hideAddOperation = false,
                             hideNoResults = false,
                             loadOptions: loadOptionsOverride,
                             inputId,
                             name,
                             stylesOverride,
                             // XPEN-3225: seeding the input from the selection makes a typed
                             // search start from the label, but it also changes `inputValue`
                             // right after the menu opens, and react-select resets the focused
                             // option to the first one when that happens. A picker whose menu
                             // should open positioned on the current choice opts out.
                             seedInputOnFocus = true,
                         }) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const selectRef = useRef(null);
    const sanitizeSearchQuery = (v = "") => {
        if (v === null || v === undefined) return "";
        return String(v).trim().replace(/\//g, "");
    };

    const getOptionFromValue = (val) => {
        if (val && typeof val === "object") {
            return optionMap(val) ?? {
                value: val,
                label: val?.name ?? String(val),
            };
        }
        return null;
    };

    const getRawValue = (opt) => {
        if (!opt) return "";
        if (opt?.value && typeof opt.value === "object") {
            return opt.value?.name ?? "";
        }
        return opt?.value ?? "";
    };

    const getDisplayValue = (opt) => {
        if (!opt) return "";
        if (opt.label) return opt.label;
        return getRawValue(opt);
    };

    const t = (key, fallback) => {
        if (data?.t?.read) return data.t.read(key);
        return fallback;
    };

    const showAddOperation = !hideAddOperation && typeof addOptionCallback === "function";

    const handleAddOption = (event) => {
        if (!showAddOperation) return;
        if (event?.preventDefault) event.preventDefault();
        if (event?.stopPropagation) event.stopPropagation();
        addOptionCallback(inputValue);
    };

    const MenuList = (props) => (
        <selectComponents.MenuList {...props}>
            {props.children}
            {showAddOperation ? (
                <div className="font-italic semi-faded mt-1">
                    <div>
                        {t("if_result_not_present", "If result not present")}
                        <span className="text-primary" role="button" onMouseDown={handleAddOption}>
                            {" "}{t("add_it", "add it")}
                        </span>
                    </div>
                </div>
            ) : null}
        </selectComponents.MenuList>
    );

    const NoOptionsMessage = (props) => {
        if (hideNoResults) return null;
        return (
            <selectComponents.NoOptionsMessage {...props}>
                <div className="font-italic semi-faded mt-1">
                    <span>
                        {t("no_results_found", "No results found")}
                        {showAddOperation ? (
                            <span className="text-primary" role="button" onMouseDown={handleAddOption}>
                                {" "}{t("add_it", "add it")}
                            </span>
                        ) : null}
                    </span>
                </div>
            </selectComponents.NoOptionsMessage>
        );
    };
    

    useEffect(() => {
        const directOption = getOptionFromValue(value);

        if (directOption) {
            setSelectedOption(directOption);
            return;
        }

        const v = sanitizeSearchQuery(value);

        if (!v) {
            setSelectedOption(null);
            return;
        }

        // When the caller drives loadOptions itself (no path-style searchUrl),
        // skip the value-resolve fetch — the value is expected to arrive as an
        // already-shaped option object handled by getOptionFromValue above.
        if (!searchUrl) {
            setSelectedOption({ value: v, label: v });
            return;
        }

        let cancelled = false;

        cachedFetch(`${searchUrl}/${encodeURIComponent(v)}`, "GET", {}, (response) => {
            if (cancelled) return;

            const list = Array.isArray(response?.data) ? response.data : [];
            const found = list.find((o) => o?.name === v || o?.unit === v) || null;
            setSelectedOption(found ? (optionMap(found) ?? { value: v, label: v }) : { value: v, label: v });
        });

        return () => {
            cancelled = true;
        };

    }, [value, searchUrl, optionMap])

    const handleChange = (opt) => {
        setSelectedOption(opt || null);
        setInputValue("");
        selectRef?.current?.blur()
        onChange?.(opt);
    };

    const loadOptions = loadOptionsOverride ?? ((inputValue = "", callback = () => {}) => {
        const v = sanitizeSearchQuery(inputValue);

        cachedFetch(`${searchUrl}/${encodeURIComponent(v)}`, "GET", {}, (response) => {
            const list = Array.isArray(response?.data) ? response.data : [];
            callback(list.map(optionMap).filter(Boolean));
        });
    });

    return (
            <AsyncSelect
                menuPortalTarget={document.body}
                isDisabled={isDisabled}
                isClearable={isClearable}
                ref={selectRef}
                cacheOptions
                defaultOptions
                loadOptions={loadOptions}
                classNamePrefix="select"
                placeholder={placeholder}
                inputId={inputId}
                name={name}
                className={`${className} ${highlightAll ? "border-highlight-secondary" : ""} border-0`.trim()}
                styles={mergeStyles(Utils.customStyleSelect, stylesOverride)}
                components={{ DropdownIndicator: null, IndicatorSeparator: null, MenuList, NoOptionsMessage }}
                formatOptionLabel={(opt, meta) => {
                    if (meta.context === "value" && !showLabelInInput) {
                        return getRawValue(opt);
                    }
                    if (typeof formatOptionLabel === "function") {
                        return formatOptionLabel(opt, meta);
                    }
                    return getDisplayValue(opt);
                }}
                value={selectedOption}
                inputValue={inputValue}
                onInputChange={(val, meta) => {
                    if (meta.action === "input-change") {
                        setInputValue(val);
                    } else if (meta.action === "menu-close") {
                        setInputValue("");
                    }
                }}
                onFocus={() => {
                    // Keep typed search human-readable when value is an internal id.
                    // If caller explicitly hides label in input, fallback to raw value.
                    if (seedInputOnFocus) {
                        setInputValue(showLabelInInput ? getDisplayValue(selectedOption) : getRawValue(selectedOption))
                    }
                    setIsFocused(true)
                }}

                onBlur={() => setIsFocused(false)}
                controlShouldRenderValue={!isFocused}
                onChange={handleChange}
            />
    );
};

export default FormAsyncSelect;
